/**
 * components/dashboard/WeeklyReportPanel.tsx — Weekly Report button + panel (v2)
 * ==================================================================================
 * v2 changes from the first version:
 *   - NO Firebase Cloud Function, NO firebase/functions import. Groq is
 *     called directly from the browser using VITE_GROQ_API_KEY — see
 *     lib/reports/llmClient.ts. On Groq failure, sections use template text.
 *   - The LLM now writes each section's explanation FROM the real numbers
 *     (not a pure template) — see lib/reports/narrateSection.ts. Every number
 *     the LLM could reference is passed in explicitly and verified after
 *     generation; any section that fails verification silently falls back
 *     to the deterministic template sentence for THAT section only.
 */

import { useEffect, useState } from 'react';
import { FileText, Loader2, RefreshCcw, FileDown, FileType2, ChevronDown, ChevronUp } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import type { TelemetryRow } from '@/types';
import type { SheetSource } from '@/config/sheets';
import { useTenant } from '@/contexts/TenantContext';
import { resolveWeeklyReportWindows } from '@/lib/reports/weekWindow';
import type { WeeklyReportPayload } from '@/lib/reports/buildWeeklyReportPayload';
import { runWeeklyReportPipeline } from '@/lib/reports/generateWeeklyReportPipeline';
import { loadSavedWeeklyReport, saveWeeklyReport } from '@/lib/reports/weeklyReportCache';
import { clearReportSentMarker } from '@/lib/reports/reportPrefs';
import { WeeklyReportPdfDocument } from './WeeklyReportPdfDocument';
import { downloadWeeklyReportDoc } from '@/lib/reports/exportWeeklyReportDoc';
import cortexHeaderLogo from '@/assets/cortex_header_logo.png';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';
import { useLanguage } from '@/hooks/useLanguage';

interface WeeklyReportPanelProps {
  meterRows: TelemetryRow[];
  sheet: SheetSource;
}

interface RenderedSection {
  title: string;
  text: string;
  tone: 'good' | 'bad' | 'neutral' | 'brown';
  source: 'llm' | 'fallback';
  /** WoW net rebate/surcharge line — only set on the comparison section. */
  impactTrend?: { text: string; tone: 'good' | 'bad' | 'brown' };
}

interface CachedReport {
  weekStart:  string; // ISO date of the report week's Monday — cache key/invalidation anchor.
  payload:    WeeklyReportPayload;
  sections:   RenderedSection[];
  health:     { title: string; explanation: string; action: string }[];
}

/**
 * Weekly report cache — in-memory only (module-scope Map, NOT localStorage/
 * sessionStorage), keyed per meter, valid for the current report week only.
 *
 * Deliberately in-memory rather than persisted to a browser storage API:
 *   - Survives navigating between pages / collapsing / re-expanding the panel
 *     within the same page load, so those never re-trigger Groq calls.
 *   - Is wiped automatically on every full page load/refresh (this module is
 *     re-evaluated from scratch), so a browser refresh always recalculates
 *     and regenerates a fresh report from the latest data — never serves a
 *     stale report the user has no way to force-clear.
 *   - Still keyed by report-week start so a report generated Monday and left
 *     open in a background tab all week gets recomputed once a new Monday
 *     actually rolls in, without needing a refresh.
 */
const weeklyReportMemoryCache = new Map<string, CachedReport>();

function readCachedReport(sheetId: string, currentWeekStartIso: string): CachedReport | null {
  const cached = weeklyReportMemoryCache.get(sheetId);
  if (!cached) return null;
  if (cached.weekStart !== currentWeekStartIso) return null; // new week — stale, ignore
  return cached;
}

function writeCachedReport(sheetId: string, entry: CachedReport): void {
  weeklyReportMemoryCache.set(sheetId, entry);
}

const toneClass = {
  good:    'text-emerald-600',
  bad:     'text-red-500',
  neutral: 'text-ink-700',
  brown:   'text-amber-900', // mixed week-over-week body
} as const;

export function WeeklyReportPanel({ meterRows, sheet }: WeeklyReportPanelProps) {
  const { tariff, contractDemandKva } = useTenant();
  const { t } = useLanguage();

  // Anchor for both the cache key and the report's own calculation window —
  // stable all week, changes exactly once when Monday rolls in.
  const currentWeekStartIso = resolveWeeklyReportWindows().reportWeek.from.toISOString();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<WeeklyReportPayload | null>(null);
  const [sections, setSections] = useState<RenderedSection[] | null>(null);
  const [health, setHealth] = useState<{ title: string; explanation: string; action: string }[] | null>(null);

  // Memory cache first; else Firestore softcopy for this meter+week (shared with email).
  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      const cached = readCachedReport(sheet.id, currentWeekStartIso);
      if (cached) {
        console.info(`[weeklyReport] Source: MEMORY CACHE · meter=${sheet.id}`);
        setPayload(cached.payload);
        setSections(cached.sections);
        setHealth(cached.health);
        setOpen(false);
        setError(null);
        return;
      }

      setPayload(null);
      setSections(null);
      setHealth(null);
      setOpen(false);
      setError(null);

      try {
        const weekStart = resolveWeeklyReportWindows().reportWeek.from;
        const saved = await loadSavedWeeklyReport(sheet.id, weekStart);
        if (cancelled) return;
        if (!saved) {
          console.info(`[weeklyReport] Source: NONE · meter=${sheet.id} (no cache, no Firestore softcopy — Generate will call LLM)`);
          return;
        }
        console.info(`[weeklyReport] Source: FIRESTORE SOFTCOPY · meter=${sheet.id} · week=${saved.weekStart}`);
        setPayload(saved.payload);
        setSections(saved.sections as RenderedSection[]);
        setHealth(saved.health);
        writeCachedReport(sheet.id, {
          weekStart: currentWeekStartIso,
          payload: saved.payload,
          sections: saved.sections as RenderedSection[],
          health: saved.health,
        });
      } catch (err) {
        console.warn('[weeklyReport] Could not load saved report:', err);
      }
    }

    void hydrate();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet.id, currentWeekStartIso]);

  function handleButtonClick() {
    // Already generated this week (cached or freshly built) — the button
    // just toggles the panel open/closed, no regeneration, no LLM calls.
    if (payload && sections) {
      setOpen(o => !o);
      return;
    }
    void generate();
  }

  /** Force a new LLM run, overwrite Firestore softcopy, update dashboard + email source. */
  function handleRefreshReport() {
    weeklyReportMemoryCache.delete(sheet.id);
    void generate({ refresh: true });
  }

  async function generate(opts?: { refresh?: boolean }) {
    console.info(`[weeklyReport] Source: FRESH GENERATE (LLM) · meter=${sheet.id}${opts?.refresh ? ' · refresh' : ''}`);
    setLoading(true);
    setError(null);
    try {
      const { reportWeek, comparisonWeek } = resolveWeeklyReportWindows();

      const { payload: computedPayload, sections: renderedSections, health: narratedHealth } =
        await runWeeklyReportPipeline({
          allRows: meterRows,
          sheet,
          tariff,
          contractDemandKva,
          reportWeek,
          comparisonWeek,
          sectionTitle: t,
        });

      setPayload(computedPayload);
      setSections(renderedSections);
      setHealth(narratedHealth);
      writeCachedReport(sheet.id, {
        weekStart: currentWeekStartIso,
        payload: computedPayload,
        sections: renderedSections,
        health: narratedHealth,
      });
      // Persist softcopy so scheduled email attaches this exact PDF.
      try {
        await saveWeeklyReport({
          meterId: sheet.id,
          weekStart: reportWeek.from,
          payload: computedPayload,
          sections: renderedSections,
          health: narratedHealth,
        });
        // After Refresh, allow the next matching slot to mail the new softcopy
        // even if this week was already marked sent.
        if (opts?.refresh) {
          await clearReportSentMarker(sheet.id).catch((err) =>
            console.warn('[weeklyReport] Could not clear sent marker:', err),
          );
        }
      } catch (err) {
        console.warn('[weeklyReport] Could not save report:', err);
      }

      setOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  }

  const hasReport = Boolean(payload && sections);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={handleButtonClick}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50 transition"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> {t('Generating weekly report…')}</>
          ) : (
            <>
              <FileText className="w-4 h-4" />
              {t('Power Analysis and Anomaly Report')}
              {hasReport && (open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleRefreshReport}
          disabled={loading}
          title={t('Regenerate report with latest data and save softcopy for email')}
          className="inline-flex items-center gap-2 rounded-xl border border-ink-200 bg-white px-3 py-2.5 text-sm font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50 transition shadow-sm"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCcw className="w-4 h-4" />
          )}
          {t('Refresh')}
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 flex items-center gap-2">
          {error}
          <button onClick={() => void generate()} className="inline-flex items-center gap-1 underline">
            <RefreshCcw className="w-3.5 h-3.5" /> {t('Retry')}
          </button>
        </div>
      )}

      {open && payload && sections && (
        <Card>
          <div className="p-5 sm:p-7 space-y-6">
            {/* Enterprise Header Lockup */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-b-2 border-brand-600 pb-4">
              {/* Left: Cortex Brand Logo */}
              <div className="flex items-center">
                <img src={cortexHeaderLogo} alt="Cortex logo" className="h-10 sm:h-12 w-auto object-contain" />
              </div>

              {/* Center: Report Title & Date Range */}
              <div className="text-center flex-1">
                <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-brand-700 uppercase">
                  {t('Weekly Impact Report')}
                </h2>
                <p className="text-xs sm:text-sm text-ink-500 font-medium mt-0.5">
                  {payload.reportWeekLabel}
                </p>
              </div>

              {/* Right: Export & Collapse Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <PDFDownloadLink
                  document={
                    <WeeklyReportPdfDocument
                      payload={payload}
                      sections={sections}
                      health={health ?? []}
                      generatedAtLabel={new Date(payload.generatedAt).toLocaleString('en-IN')}
                    />
                  }
                  fileName={`Weekly_Impact_Report_${payload.reportWeekLabel.replace(/[^\w-]+/g, '_')}.pdf`}
                >
                  {({ loading }) => (
                    <button
                      type="button"
                      disabled={loading}
                      title={t('Download PDF')}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50 transition shadow-sm"
                    >
                      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                      PDF
                    </button>
                  )}
                </PDFDownloadLink>
                <button
                  type="button"
                  title={t('Download Word document')}
                  onClick={() => downloadWeeklyReportDoc(payload, sections, health ?? [], new Date(payload.generatedAt).toLocaleString('en-IN'))}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50 transition shadow-sm"
                >
                  <FileType2 className="w-3.5 h-3.5" />
                  DOC
                </button>
                <button
                  type="button"
                  title={t('Collapse')}
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-1 rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50 transition shadow-sm"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Enterprise Information Card (Responsive 2-Column Grid) */}
            <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 shadow-xs space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 text-sm pb-2.5 border-b border-gray-100 items-center">
                <div className="sm:col-span-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{t('Consumer Facility')}</div>
                <div className="sm:col-span-8 font-semibold text-gray-900 flex items-center flex-wrap gap-1.5">
                  {(() => {
                    const match = payload.facility.consumer.match(/^(.*?)\s*(\(Consumer ID:.*?\))$/i);
                    if (match) {
                      return (
                        <>
                          <span className="font-bold text-gray-900">{match[1]}</span>
                          <span className="text-xs font-normal text-gray-500">{match[2]}</span>
                        </>
                      );
                    }
                    return <span>{payload.facility.consumer}</span>;
                  })()}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 text-sm pb-2.5 border-b border-gray-100 items-center">
                <div className="sm:col-span-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{t('DISCOM / Tariff')}</div>
                <div className="sm:col-span-8 font-semibold text-gray-900">{payload.facility.discomTariff}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 text-sm pb-2.5 border-b border-gray-100 items-center">
                <div className="sm:col-span-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{t('Sanctioned CD')}</div>
                <div className="sm:col-span-8 font-semibold text-gray-900">{payload.facility.contractDemandKva} kVA</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 text-sm items-center">
                <div className="sm:col-span-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{t('Tariff Schedule')}</div>
                <div className="sm:col-span-8 font-semibold text-gray-900">{payload.facility.tariffRatesLabel}</div>
              </div>
            </div>

            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wide text-brand-700">{t('Billing & Utilisation Impact')}</h3>
              {sections.map(s => (
                <div key={s.title}>
                  <p className="text-sm font-semibold text-ink-800 mb-1 border-b border-brand-200 inline-block pb-0.5">{s.title}</p>
                  <p className={cn('text-sm leading-relaxed font-medium', toneClass[s.tone])}>{s.text}</p>
                  {s.impactTrend && (
                    <p className={cn('text-sm leading-relaxed font-medium mt-1', toneClass[s.impactTrend.tone])}>
                      {s.impactTrend.text}
                    </p>
                  )}
                </div>
              ))}
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wide text-brand-700">{t('Live Electrical Health Monitoring')}</h3>
              {!health || health.length === 0 ? (
                <p className="text-sm text-ink-500">{t('No significant electrical health findings this week.')}</p>
              ) : (
                health.map((h, i) => (
                  <div key={h.title} className="space-y-1">
                    <p className="text-sm font-bold text-ink-900">{i + 1}. {h.title}</p>
                    <p className="text-sm text-ink-700">{h.explanation}</p>
                    <p className="text-sm text-ink-600 bg-ink-50 border-l-2 border-brand-400 pl-3 py-1.5 rounded-r">
                      <span className="font-semibold text-brand-700">{t('Recommended action')}: </span>{h.action}
                    </p>
                  </div>
                ))
              )}
            </section>
          </div>
        </Card>
      )}
    </div>
  );
}
